/* ═══════════════════════════════════════════════════════════
   ATELIER THEME — Global JavaScript
   ═══════════════════════════════════════════════════════════ */

/* ── Theme Namespace ───────────────────────────────────── */
window.Atelier = window.Atelier || {};

/* ── Header Scroll Behavior ────────────────────────────── */
/* Header is position:sticky (CSS) → siempre fijo al desplazar. Aquí solo
   añadimos una sombra al hacer scroll. Batched en un rAF por ráfaga para no
   forzar recalc en cada evento de scroll (INP). Sin translateY: no se oculta. */
(function() {
  const header = document.querySelector('.header');
  if (!header) return;

  let ticking = false;

  function update() {
    header.classList.toggle('header--scrolled', window.scrollY > 50);
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });
})();

/* ── Cart Drawer ───────────────────────────────────────── */
Atelier.CartDrawer = class {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer__overlay');
    if (!this.drawer) return;

    this.bindEvents();
  }

  bindEvents() {
    // Open triggers
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close triggers (delegado: sobrevive al refresco del drawer)
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-close]')) this.close();
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.drawer.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  isOpen() {
    return this.drawer.classList.contains('is-open');
  }
};

/* ── Refresco del Cart Drawer (Section Rendering API) ──────
   Reemplaza solo el innerHTML de .cart-drawer con el drawer re-renderizado.
   Conserva el elemento externo (estado is-open) y el overlay. Los controles
   internos (cerrar, cantidades) funcionan por delegación tras el swap. */
Atelier.refreshCartDrawer = function () {
  const live = document.querySelector('.cart-drawer');
  if (!live) return Promise.resolve();
  const url = window.location.pathname + '?section_id=sp-cart-drawer';
  return fetch(url, { headers: { 'Accept': 'text/html' } })
    .then((r) => { if (!r.ok) throw new Error('section fetch failed'); return r.text(); })
    .then((html) => {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer');
      if (fresh) live.innerHTML = fresh.innerHTML;
    })
    .catch(() => { /* red caída: se deja el drawer como está */ });
};

/* ── Contador del carrito en el header (fuente única) ────── */
Atelier.updateCartCount = function (n) {
  const toggle = document.querySelector('[data-cart-toggle]');
  if (!toggle) return;
  let badge = toggle.querySelector('[data-cart-count]');
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'header__cart-count';
    badge.setAttribute('data-cart-count', '');
    toggle.appendChild(badge);
  }
  badge.textContent = n;
  badge.style.display = n > 0 ? '' : 'none';
  badge.classList.remove('is-bump');
  void badge.offsetWidth; /* reinicia la animación */
  badge.classList.add('is-bump');
};

/* ── Refresco de la PÁGINA de carrito (/cart) ──────────────
   No-op si no hay .cart-page en la página actual. Re-renderiza el contenido
   con el estado real del servidor. */
Atelier.refreshCartPage = function () {
  const page = document.querySelector('.cart-page');
  if (!page) return Promise.resolve();
  return fetch(window.location.pathname, { headers: { 'Accept': 'text/html' } })
    .then((r) => { if (!r.ok) throw new Error('cart page fetch failed'); return r.text(); })
    .then((html) => {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-page');
      if (fresh) page.innerHTML = fresh.innerHTML;
    })
    .catch(() => {});
};

/* ── Punto único de sincronización tras cualquier mutación ──
   Actualiza el contador y refresca TODAS las vistas del carrito presentes
   (drawer + página). Cada refresco es no-op si su vista no está en la página.
   Esto evita que el drawer y /cart se desincronicen. */
Atelier.onCartChanged = function (cart) {
  if (cart && typeof cart.item_count !== 'undefined') Atelier.updateCartCount(cart.item_count);
  return Promise.all([Atelier.refreshCartDrawer(), Atelier.refreshCartPage()]);
};

/* ── Cantidades del carrito (líneas del drawer) ───────────
   Escucha 'change' en inputs con data-line (solo los del drawer; el selector
   del producto NO tiene data-line). Sincroniza con /cart/change.js y refresca.
   Los +/- disparan 'change' vía QuantitySelector, así que este handler cubre
   tanto los botones como la edición manual del número. */
(function () {
  let changing = false;
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input][data-line]');
    if (!input || !input.closest('.cart-drawer') || changing) return;

    const line = parseInt(input.getAttribute('data-line'), 10);
    let qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 1) { qty = 1; input.value = 1; }

    changing = true;
    const drawer = document.querySelector('.cart-drawer');
    if (drawer) drawer.classList.add('is-updating');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty })
    })
      .then((r) => { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then((cart) => Atelier.onCartChanged(cart))
      .catch(() => Atelier.onCartChanged()) /* revertir a estado real del carrito */
      .then(() => { changing = false; if (drawer) drawer.classList.remove('is-updating'); });
  });
})();

/* ── Eliminar línea del carrito (botón del drawer) ────────
   quantity: 0 en /cart/change.js elimina la línea. Reutiliza el refresco. */
(function () {
  let removing = false;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn || removing) return;

    const line = parseInt(btn.getAttribute('data-cart-remove'), 10);
    removing = true;
    const drawer = document.querySelector('.cart-drawer');
    if (drawer) drawer.classList.add('is-updating');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    })
      .then((r) => { if (!r.ok) throw new Error('remove failed'); return r.json(); })
      .then((cart) => Atelier.onCartChanged(cart))
      .catch(() => Atelier.onCartChanged())
      .then(() => { removing = false; if (drawer) drawer.classList.remove('is-updating'); });
  });
})();

/* ── Scroll Animations (IntersectionObserver) ──────────── */
Atelier.ScrollAnimations = class {
  constructor() {
    this.elements = document.querySelectorAll('[data-animate]');
    if (!this.elements.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    this.elements.forEach(el => {
      el.style.opacity = '0';
      this.observer.observe(el);
    });
  }
};

/* ── Product Card Hover (secondary image) ──────────────── */
Atelier.ProductCardHover = class {
  constructor() {
    document.querySelectorAll('.product-card').forEach(card => {
      const primaryImg = card.querySelector('.product-card__primary-image');
      const secondaryImg = card.querySelector('.product-card__secondary-image');
      if (!primaryImg || !secondaryImg) return;

      // Preload secondary image on hover
      card.addEventListener('mouseenter', () => {
        if (secondaryImg.dataset.src && !secondaryImg.src) {
          secondaryImg.src = secondaryImg.dataset.src;
        }
      });
    });
  }
};

/* ── Quantity Selector (delegado: sobrevive al refresco del drawer) ── */
Atelier.QuantitySelector = class {
  constructor() {
    document.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-qty-minus]');
      const plus = e.target.closest('[data-qty-plus]');
      if (!minus && !plus) return;

      const selector = (minus || plus).closest('.quantity-selector');
      const input = selector && selector.querySelector('[data-qty-input]');
      if (!input) return;

      let val = parseInt(input.value) || 1;
      if (minus && val > 1) val -= 1;
      else if (plus) val += 1;
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
};

/* ── Initialize ────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  new Atelier.CartDrawer();
  new Atelier.ScrollAnimations();
  new Atelier.ProductCardHover();
  new Atelier.QuantitySelector();
});
